import React, { useContext } from 'react';
import { OTAContext } from '../../../context/OTAContext';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';

import UpdateIcon from '@mui/icons-material/Update';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import Title from '../../resources/Title';
import * as Constants from '../../../components/api/constants';
import fileDownload from 'js-file-download';

export default function OTAFirmware() {
  const state = useContext(OTAContext);

  const latestVersion =
    state?.version && state.version !== ''
      ? state.version
      : undefined;

  const defaultVersion = Array.isArray(state?.firmwareHistory)
    ? state.firmwareHistory.find((f) => f.default)?.version
    : undefined;

  const downloadFirmware = () => {
    if (!latestVersion) return;

    const requestOptions = {
      method: 'GET',
      credentials: 'include',
    };

    fetch(`${Constants.baseURL}${Constants.apiFirmwareDownload}/0`, requestOptions)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Download failed');
        }
        return response.blob();
      })
      .then((blob) => {
        fileDownload(blob, `V${latestVersion}.bin`);
      })
      .catch((error) => {
        console.log('request failed', error);
      });
  };

  return (
    <React.Fragment>
      <Typography
        component="h2"
        variant="h6"
        color="white"
        sx={{ fontWeight: 600, mb: 2 }}
      >
        Firmware
      </Typography>

      <Grid container spacing={2}>
        {/* LATEST FIRMWARE */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: 293,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(144,202,249,0.18)',
                  color: '#90CAF9',
                  flexShrink: 0,
                }}
              >
                <UpdateIcon fontSize="small" />
              </Box>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}
                >
                  Latest Firmware
                </Typography>
                <Box sx={{ width: 90, mt: 1, mb: 1 }}>
                  <Chip
                    label="Latest"
                    size="small"
                    color="primary"
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      height: 22,
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}
                >
                  Most recently uploaded firmware version
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 'auto',
                mb: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  border: '1px solid rgba(255,255,255,0.4)',
                  bgcolor: '#000',
                  minWidth: 180,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {latestVersion || 'No firmware uploaded'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* DEFAULT FIRMWARE */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: 293,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(129, 199, 132, 0.18)',
                  color: '#81C784',
                  flexShrink: 0,
                }}
              >
                <BookmarkIcon fontSize="small" />
              </Box>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}
                >
                  Default Firmware
                </Typography>
                <Box sx={{ width: 90, mt: 1, mb: 1 }}>
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label="Default"
                    size="small"
                    color="success"
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      height: 22,
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}
                >
                  Current version available for download
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 'auto',
                mb: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  border: '1px solid rgba(255,255,255,0.4)',
                  bgcolor: '#000',
                  minWidth: 180,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {defaultVersion || 'No default firmware'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
